import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LoginModal from './LoginModal';
import './ScoreReport.css';
import { storage } from '../utils/storage';
import { API_ENDPOINTS } from '../config/api';

const ScoreReport = ({ 
  currentRound,  // 当前轮次的对话数据
  selectedPart = 'part1', // 'part1', 'part2', 'part3'
  username = '考生',
  userData = null  // 接收从父组件传递的用户数据
}) => {
  const { t, language } = useLanguage();
  // 确保标签可以切换，使用三个独立的状态变量控制显示
  const [showRecords, setShowRecords] = useState(true);
  const [showScores, setShowScores] = useState(true); // 默认也显示分数分析
  const [showSuggestions, setShowSuggestions] = useState(true); // 默认也显示建议
  
  // 添加API生成的分析和建议状态
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiCallStatus, setApiCallStatus] = useState({
    called: false,
    success: false,
    message: '',
    timestamp: null
  });
  
  // 在useState部分添加overallFeedback状态
  const [overallFeedback, setOverallFeedback] = useState("");
  
  // 添加用户登录状态和模态框状态
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 添加会员状态
  const [isPaidMember, setIsPaidMember] = useState(false);
  
  // 修改 useEffect 钩子，在用户是有效付费会员时自动调用生成报告函数
  useEffect(() => {
    console.log('ScoreReport组件加载，检查用户状态...');
    
    // 使用传入的userData来设置用户状态，避免重复读取localStorage
    if (userData && (userData.isRegistered || userData.isLoggedIn)) {
      setIsLoggedIn(true);
      console.log('用户已登录:', userData);
      
      // 检查用户是否为付费会员
      if (userData.isPaid) {
        setIsPaidMember(true);
        console.log('用户是付费会员');
      } else {
        setIsPaidMember(false);
        console.log('用户不是付费会员');
      }
    } else {
      // 如果没有传入userData或用户未注册，检查localStorage
      console.log('未收到有效用户数据，检查localStorage...');
      const storedUser = localStorage.getItem('talktastic_user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user && (user.isRegistered || user.isLoggedIn)) {
            setIsLoggedIn(true);
            console.log('从localStorage读取到登录用户:', user);
            
            // 检查用户是否为付费会员
            if (user.isPaid) {
              setIsPaidMember(true);
              console.log('用户是付费会员');
            } else {
              setIsPaidMember(false);
              console.log('用户不是付费会员');
            }
          } else {
            console.log('localStorage中的用户未登录或未注册');
            setShowLoginModal(true);
          }
        } catch (error) {
          console.error('读取用户信息失败:', error);
          setShowLoginModal(true);
        }
      } else {
        console.log('localStorage中无用户信息');
        setShowLoginModal(true);
      }
    }

    // 如果用户是付费会员，自动生成报告
    const canGenerateReport = isLoggedIn && isPaidMember && 
                             currentRound && currentRound.messages && currentRound.messages.length > 0;
    
    console.log('生成报告条件检查:', {
      isLoggedIn,
      isPaidMember,
      hasCurrentRound: !!currentRound,
      canGenerateReport
    });
    
    if (canGenerateReport && !loading && !apiCallStatus.called) {
      console.log('满足自动生成报告条件，开始生成...');
      // 添加短延时确保状态已更新
      setTimeout(() => generateAnalysisAndSuggestions(), 500);
    }
  }, [userData, isLoggedIn, isPaidMember, currentRound, loading, apiCallStatus.called]);
  
  // 处理登录成功
  const handleLoginSuccess = (user) => {
    console.log('登录成功，用户信息:', user);
    setIsLoggedIn(true);
    setShowLoginModal(false);
    
    // 检查是否为付费会员
    if (user && user.isPaid) {
      setIsPaidMember(true);
    }
    
    // 登录成功后不自动生成报告，等待用户手动触发
    console.log('登录成功，用户可以手动生成报告');
  };
  
  // 处理关闭登录模态框
  const handleCloseLoginModal = () => {
    console.log('用户关闭了登录模态框');
    setShowLoginModal(false);
    // 确保在UI上显示需要登录的提示信息
    setIsLoggedIn(false);
  };
  
  // 标签切换函数
  const switchToRecords = () => {
    setShowRecords(true);
    setShowScores(false);
    setShowSuggestions(false);
  };
  
  const switchToScores = () => {
    setShowRecords(false);
    setShowScores(true);
    setShowSuggestions(false);
  };
  
  const switchToSuggestions = () => {
    setShowRecords(false);
    setShowScores(false);
    setShowSuggestions(true);
  };
  
  // 新增: 调用API生成分析和建议
  const generateAnalysisAndSuggestions = async () => {
    // 显式设置加载状态为true
    setLoading(true);
    setError(null);
    
    try {
      console.log('生成分析和建议中...');
      
      // API key 由后端持有，前端无需获取
      
      // 准备会话数据 - 提取问题和答案对
      let questionAnswerPairs = [];
      if (currentRound && currentRound.messages && currentRound.messages.length > 0) {
        const messages = currentRound.messages;
        
        console.log('开始提取问答对，消息总数:', messages.length);
        
        // 提取问题-答案对
        for (let i = 0; i < messages.length; i++) {
          const msg = messages[i];
          
          // 考官消息的条件：type 是 mentor，且不是欢迎消息/系统消息
          const isMentorQuestion = msg.type === 'mentor' && 
                                  msg.content && 
                                  !msg.content.includes('Welcome to') &&
                                  !msg.content.includes('欢迎') &&
                                  !msg.content.includes('考官思考中') &&
                                  !msg.content.includes('thinking');
          
          if (isMentorQuestion) {
            const question = msg.content;
            // 查找下一个用户回答
            let answer = '';
            for (let j = i + 1; j < messages.length; j++) {
              if (messages[j].type === 'user' && messages[j].content) {
                answer = messages[j].content.trim();
                break;
              }
            }
            
            if (answer && answer.length > 5) { // 确保答案有实际内容
              console.log('找到问答对:', { question: question.substring(0, 50), answer: answer.substring(0, 50) });
              questionAnswerPairs.push({
                question: question,
                answer: answer
              });
            }
          }
        }
      } else {
        throw new Error('没有对话数据可供分析');
      }
      
      console.log('提取到的问答对数量:', questionAnswerPairs.length);
      
      if (questionAnswerPairs.length === 0) {
        console.error('消息列表:', currentRound?.messages);
        throw new Error('未找到有效的问答对，请确保完成了语音回答');
      }
      
      // 格式化问答对文本
      const qaText = questionAnswerPairs.map((qa, idx) => 
        `【问题${idx + 1}】${qa.question}\n【考生回答${idx + 1}】${qa.answer}`
      ).join('\n\n');
      
      // 设计提示词 - 根据语言选择
      const isEnglish = language === 'en';
      const prompt = isEnglish 
        ? `As a professional IELTS speaking assessor, please analyze the candidate's ${getModuleName()} performance and provide detailed evaluation and targeted improvement suggestions.

Q&A Content:
${qaText}

Please provide detailed analysis in JSON format:
1. Fluency and Coherence Analysis (fluency) - analyze speaking speed, pauses, repetitions
2. Lexical Resource Analysis (vocabulary) - analyze vocabulary range, accuracy, advanced expressions
3. Grammar Range and Accuracy Analysis (grammar) - analyze grammar structures, tense usage, sentence variety
4. Optimized answers for each question (optimizedAnswers) - must be an array format

⚠️ Important Requirements:
- optimizedAnswers must be an array with ${questionAnswerPairs.length} elements
- Each element corresponds to one question's optimization
- Optimized answers should retain core content and viewpoints from original answers
- Enhance to IELTS Band 7-8 level expression
- All content must be in English

Return only JSON data, no other text. Use this structure:
{
  "fluency": "Fluency analysis (within 150 words)...",
  "vocabulary": "Vocabulary analysis (within 150 words)...",
  "grammar": "Grammar analysis (within 150 words)...",
  "optimizedAnswers": [
    {
      "questionNumber": 1,
      "question": "Original question",
      "originalAnswer": "Candidate's original answer",
      "improvedAnswer": "Optimized answer (retain core ideas, enhance expression quality)"
    },
    {
      "questionNumber": 2,
      "question": "Original question",
      "originalAnswer": "Candidate's original answer",
      "improvedAnswer": "Optimized answer"
    }
  ]
}`
        : `作为一名专业雅思口语评估专家，请分析以下${getModuleName()}的考生表现，提供详细评估和针对性的改进建议。

问答内容：
${qaText}

请提供以下内容的详细分析（JSON格式）：
1. 流利度和连贯性分析 (fluency) - 分析考生的语速、停顿、重复等
2. 词汇资源分析 (vocabulary) - 分析词汇的丰富度、准确性、高级表达
3. 语法多样性和准确性分析 (grammar) - 分析语法结构、时态使用、句式变化
4. 针对每个问题的优化答案 (optimizedAnswers) - 必须是数组格式，包含每个问题的优化

⚠️ 重要要求：
- optimizedAnswers 必须是数组，包含${questionAnswerPairs.length}个元素
- 每个元素对应一个问题的优化答案
- 优化答案需要保留原答案的核心内容和观点
- 提升到雅思7-8分水平的表达
- **分析部分（fluency, vocabulary, grammar）使用中文**
- **优化答案（improvedAnswer）必须使用英文**，因为雅思考试是英文的

仅返回JSON格式数据，不要包含其他文本。使用以下结构：
{
  "fluency": "流利度分析（150字以内）...",
  "vocabulary": "词汇分析（150字以内）...",
  "grammar": "语法分析（150字以内）...",
  "optimizedAnswers": [
    {
      "questionNumber": 1,
      "question": "Original question in English",
      "originalAnswer": "Candidate's original answer in English",
      "improvedAnswer": "Optimized English answer (retain core ideas, enhance to Band 7-8 level)"
    },
    {
      "questionNumber": 2,
      "question": "Original question in English",
      "originalAnswer": "Candidate's original answer in English",
      "improvedAnswer": "Optimized English answer"
    }
  ]
}`;

      console.log('正在调用DeepSeek API...');
      
      // 调用DeepSeek API
      const response = await fetch('/api/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 2500  // 增加token限制以支持多个优化答案
        })
      });
      
      const data = await response.json();
      console.log('API响应:', data);
      
      // 检查响应是否成功
      if (!response.ok) {
        throw new Error(`API调用失败: ${data.error?.message || '未知错误'}`);
      }
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('API返回格式不正确');
      }
      
      // 解析API返回的内容
      let analysisContent;
      try {
        const content = data.choices[0].message.content.trim();
        // 尝试提取JSON内容，去除可能的额外文本
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          analysisContent = JSON.parse(jsonMatch[0]);
        } else {
          analysisContent = JSON.parse(content);
        }
      } catch (parseError) {
        console.error('JSON解析错误:', parseError);
        throw new Error('无法解析API返回的内容');
      }
      
      // 保存到本地存储并更新状态
      storage.set('lastAnalysis', analysisContent);
      
      setAnalysis(analysisContent);
      setApiCallStatus({
        called: true,
        success: true,
        message: '分析成功完成',
        timestamp: new Date()
      });
      
      // 更新整体反馈
      if (language === 'en') {
        setOverallFeedback("Based on the detailed analysis, here are optimized answers to help you reach Band 7+ level.");
      } else {
        setOverallFeedback("根据详细分析，以下是帮助您达到7+分水平的优化答案。");
      }
      
    } catch (error) {
      console.error('分析错误:', error);
      setError(error.message);
      setApiCallStatus({
        called: true,
        success: false,
        message: error.message || '分析失败',
        timestamp: new Date()
      });
    } finally {
      // 确保无论成功或失败都关闭加载状态
      setLoading(false);
    }
  };
  
  const saveGameResult = async (result) => {
    try {
      const token =
        localStorage.getItem('talktastic_token') ||
        localStorage.getItem('token');

      const response = await fetch(API_ENDPOINTS.saveGameResult, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(result)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message =
          errorData?.message || `保存失败，状态码: ${response.status}`;
        throw new Error(message);
      }

      const history = storage.get('gameHistory') || [];
      history.unshift({
        ...result,
        timestamp: new Date().toISOString()
      });
      storage.set('gameHistory', history);
    } catch (error) {
      console.error('保存游戏结果错误:', error);
    }
  };
  
  // 示例语法错误标记函数
  const highlightGrammarErrors = (text) => {
    // 这里应该有更复杂的语法分析逻辑
    // 现在只是简单示例，标记一些常见错误
    const commonErrors = [
      { error: 'is go', correct: 'is going' },
      { error: 'yesterday I go', correct: 'yesterday I went' },
      { error: 'she have', correct: 'she has' },
      { error: 'they is', correct: 'they are' },
      { error: 'I has', correct: 'I have' }
    ];
    
    let highlightedText = text;
    
    commonErrors.forEach(({ error, correct }) => {
      // 使用正则表达式匹配错误，忽略大小写
      const regex = new RegExp(error, 'gi');
      highlightedText = highlightedText.replace(
        regex, 
        `<span class="grammar-error" title="建议修改为: ${correct}">$&</span>`
      );
    });
    
    return highlightedText;
  };
  
  // 示例评分数据 - 基于所选模块
  const [currentModuleScore, setCurrentModuleScore] = useState({
    fluency: 7.0,
    vocabulary: 6.5,
    grammar: 6.0,
    total: 6.5
  });
  
  // 根据分数确定颜色
  const getScoreColor = (score) => {
    if (score >= 7.5) return 'green';
    if (score >= 6.5) return 'blue';
    if (score >= 5.5) return 'orange';
    return 'red';
  };
  
  // 根据分数计算进度条宽度百分比
  const getScorePercentage = (score) => {
    return (score / 9) * 100;
  };
  
  // 示例分析数据 - 每个模块可以有不同的分析内容
  const mockAnalysisEN = {
    part1: {
      fluency: "Your response contains some pauses and filler words (such as \"um\", \"uh\"), which affects overall fluency. In Part 1 short-answer questions, it's recommended to keep answers concise and clear, reducing unnecessary pauses.",
      vocabulary: "Vocabulary usage is generally accurate, but could be more varied when describing daily activities. For example, you could use \"occasionally\" instead of \"sometimes\", or \"venture\" instead of \"go\".",
      grammar: "Main grammatical errors are concentrated in tense usage. Specific errors include:\n1. Present continuous misuse: 'I is go'\n2. Past tense errors: 'Yesterday I go'"
    },
    part2: {
      fluency: "Your response contains some pauses and filler words (such as \"um\", \"uh\"), which affects overall fluency. In terms of coherence, some sentences lack appropriate connectors, leading to logical jumps.",
      vocabulary: "Vocabulary usage is generally accurate, but lacks diversity and precision when describing personality and influence. Consider using more specific adjectives to enrich expression. For example, replace \"very knowledgeable\" with \"erudite\" or \"insightful\".",
      grammar: "Main grammatical errors are concentrated in subject-verb agreement and tense usage. Specific errors include:\n1. Pronoun gender confusion: 'my grandfather... She have been'\n2. Subject-verb disagreement: 'they is very knowledgeable'\n3. Auxiliary verb errors: 'I has learned'"
    },
    part3: {
      fluency: "In Part 3 in-depth discussion, your response fluency needs improvement. Transitions between viewpoints are not natural enough, sometimes lacking logical coherence, leading to unclear expression of ideas.",
      vocabulary: "When discussing abstract concepts and social issues, vocabulary performance is relatively basic. It's recommended to increase topic-related professional vocabulary and academic expressions to demonstrate deeper thinking.",
      grammar: "Few complex sentence patterns are used, mainly simple and compound sentences. Consider increasing the use of complex sentences and clauses to demonstrate grammatical diversity and complexity of thought."
    }
  };

  const mockAnalysisCN = {
    part1: {
      fluency: "您的回答中存在一些停顿和语气词（如\"um\"、\"uh\"），影响了整体流畅度。在Part 1简短问答中，建议保持回答简洁明了，减少不必要的停顿。",
      vocabulary: "词汇使用基本准确，但在描述日常活动时可以使用更多样化的表达。例如，可以使用\"occasionally\"替代\"sometimes\"，使用\"venture\"替代\"go\"。",
      grammar: "主要语法错误集中在时态使用上。具体错误包括：\n1. 现在进行时误用：'I is go'\n2. 过去时态错误：'Yesterday I go'"
    },
    part2: {
      fluency: "您的回答中存在一些停顿和语气词（如\"um\"、\"uh\"），影响了整体流畅度。连贯性方面，部分句子之间缺乏适当的连接词，导致逻辑跳跃。",
      vocabulary: "词汇使用基本准确，但在描述人物性格和影响方面缺乏多样性和精确性。可以考虑使用更具体的形容词来丰富表达。例如，将\"very knowledgeable\"替换为\"erudite\"或\"insightful\"。",
      grammar: "主要语法错误集中在主谓一致和时态使用上。具体错误包括：\n1. 代词性别混淆：'my grandfather... She have been'\n2. 主谓不一致：'they is very knowledgeable'\n3. 助动词错误：'I has learned'"
    },
    part3: {
      fluency: "在Part 3的深入讨论中，您的回答流畅度有待提高。观点之间的过渡不够自然，有时缺乏逻辑连贯性，导致观点表达不够清晰。",
      vocabulary: "在讨论抽象概念和社会议题时，词汇表现较为基础。建议增加主题相关的专业词汇和学术表达，以展示更深入的思考能力。",
      grammar: "复杂句型使用较少，主要使用简单句和并列句。建议增加复合句、从句的使用，以展示语法多样性和思维的复杂性。"
    }
  };
  
  // 示例优化答案数据 - 为每个模块提供不同的示例
  const mockOptimizedAnswersEN = {
    part1: {
      original: "I usually go to the park on weekends. I like it because there are many trees and I can relax. Sometimes I go with my friends and we play games. The weather is usually good.",
      improved: "I tend to spend my weekends at the local botanical park, which I find particularly appealing due to its lush greenery and tranquil atmosphere. It offers a perfect escape from the hustle and bustle of city life. Occasionally, I'm accompanied by friends, and we engage in various recreational activities. The pleasant climate in our region makes outdoor excursions quite enjoyable most of the time."
    },
    part2: {
      original: "I want to talk about my grandfather. He is very knowledgeable and teaches me many things. He worked as a doctor for 40 years and helped many people. He is very kind and always listens to my problems. I learned many life lessons from him.",
      improved: "I'd like to describe my grandfather, who has been a significant influence in my life. He's remarkably erudite and has imparted considerable wisdom to me over the years. Having dedicated four decades of his life to the medical profession, he's positively impacted countless individuals through his work. His compassionate nature and willingness to offer a sympathetic ear whenever I'm facing challenges are qualities I deeply admire. Through his guidance, I've acquired invaluable perspectives on navigating life's complexities."
    },
    part3: {
      original: "I think family relationships are changing because people are busy now. Many young people live far from parents because of work. Technology helps us stay connected but it's not the same as being together. Families used to eat together every day but now it's maybe only on holidays.",
      improved: "In my opinion, family dynamics have undergone significant transformations largely due to the increasingly demanding pace of modern life. Career opportunities often necessitate geographical separation between younger generations and their parents, creating physical distance within families. While technological advancements facilitate communication across distances, they cannot fully replicate the emotional connection established through physical presence. There's been a noticeable shift in family traditions as well; whereas daily family meals were once commonplace, now such gatherings are frequently reserved for special occasions and holidays. This reduction in face-to-face interaction potentially impacts the depth of familial bonds."
    }
  };

  const mockOptimizedAnswersCN = {
    part1: {
      original: "我周末通常去公园。我喜欢那里因为有很多树，我可以放松。有时候我和朋友一起去，我们玩游戏。天气通常很好。",
      improved: "周末我倾向于去当地的植物园放松心情，那里的环境特别吸引我，主要是因为园内茂密的绿植和宁静的氛围，为我提供了一个远离城市喧嚣的完美去处。偶尔，我会邀上几位好友同行，我们一起参与各种有趣的休闲活动。我们地区宜人的气候使得户外活动在大多数时候都非常惬意。"
    },
    part2: {
      original: "我想谈谈我的爷爷。他很有知识，教我很多东西。他做了40年的医生，帮助了很多人。他很善良，总是听我的问题。我从他那里学到了很多人生课程。",
      improved: "我想描述一下我的祖父，他对我的人生产生了重大影响。他学识渊博，多年来向我传授了大量的智慧。他将四十年的职业生涯献给了医疗事业，通过工作积极地影响了无数人的生活。我深深敬佩他富有同情心的天性，以及每当我面临挑战时，他总是愿意倾听和支持我的品质。在他的指导下，我获得了对应对生活复杂性的宝贵见解和人生智慧。"
    },
    part3: {
      original: "我认为家庭关系正在改变，因为人们现在很忙。许多年轻人因为工作住得离父母很远。技术帮助我们保持联系，但这与在一起不一样。以前家人每天一起吃饭，但现在可能只有在假期才一起吃。",
      improved: "在我看来，家庭动态已经发生了显著的转变，这主要归因于现代生活日益紧张的节奏。职业发展机会经常导致年轻一代与父母之间产生地理距离，造成家庭成员之间的物理分离。虽然科技进步促进了远距离沟通，但它无法完全复制通过物理存在建立的情感连接。家庭传统也发生了明显的变化：过去家庭成员每天共进晚餐是很常见的，而现在这样的聚会通常仅限于特殊场合和节假日。这种面对面互动的减少可能会影响家庭纽带的深度和质量。"
    }
  };
  
  // 获取当前模块的分析和建议 - 优先使用API生成的内容
  const mockAnalysis = language === 'en' ? mockAnalysisEN : mockAnalysisCN;
  const mockSuggestions = language === 'en' ? mockOptimizedAnswersEN : mockOptimizedAnswersCN;
  
  // 使用生成的分析或备用的模拟数据
  const currentAnalysis = analysis || mockAnalysis[selectedPart];
  // const currentSuggestions = generatedSuggestions || mockSuggestions[selectedPart];
  
  // 添加对优化答案的访问
  const mockOptimizedAnswers = language === 'en' ? mockOptimizedAnswersEN : mockOptimizedAnswersCN;
  // 修复这一行，确保正确获取API返回的优化答案
  const currentOptimizedAnswers = analysis && analysis.optimizedAnswers ? analysis.optimizedAnswers : mockOptimizedAnswers[selectedPart];
  
  // 获取模块名称
  const getModuleName = () => {
    if (language === 'en') {
      switch(selectedPart) {
        case 'part1': return 'Part 1 - Introduction and Interview';
        case 'part2': return 'Part 2 - Long Turn';
        case 'part3': return 'Part 3 - Discussion';
        default: return 'Part 1 - Introduction and Interview';
      }
    } else {
      switch(selectedPart) {
        case 'part1': return 'Part 1 - 简短问答';
        case 'part2': return 'Part 2 - 长篇演讲';
        case 'part3': return 'Part 3 - 深入讨论';
        default: return 'Part 1 - 简短问答';
      }
    }
  };
  
  const renderAnswerOptimization = () => {
    console.log("渲染优化答案，当前数据:", currentOptimizedAnswers);
    
    // 确保有优化答案可用
    if (!currentOptimizedAnswers) {
      console.error("没有可用的优化答案数据");
      return (
        <div className="answer-optimization">
          <h4>{language === 'en' ? 'Optimized Answers' : '优化版本答案'}</h4>
          <div className="optimization-container">
            <div className="improved-answer">
              <div className="answer-content">
                {language === 'en' ? 'No optimized answer available.' : '暂无可用的优化答案。'}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // 检查是否是数组格式（新格式）
    if (Array.isArray(currentOptimizedAnswers)) {
      return (
        <div className="answer-optimization">
          <h4>{language === 'en' ? 'Optimized Answers' : '优化版本答案'}</h4>
          <div className="optimization-list">
            {currentOptimizedAnswers.map((item, index) => (
              <div key={index} className="optimization-item" style={{
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: '16px',
                  marginBottom: '12px',
                  color: '#495057'
                }}>
                  {language === 'en' ? `Question ${item.questionNumber || index + 1}` : `问题 ${item.questionNumber || index + 1}`}
                </div>
                
                {item.question && (
                  <div style={{
                    marginBottom: '15px',
                    padding: '12px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <strong>{language === 'en' ? '🎤 Question: ' : '🎤 问题：'}</strong>
                    {item.question}
                  </div>
                )}
                
                {item.originalAnswer && (
                  <div style={{
                    marginBottom: '15px',
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    borderLeft: '3px solid #ffc107'
                  }}>
                    <strong>{language === 'en' ? '📝 Your Answer: ' : '📝 您的回答：'}</strong>
                    {item.originalAnswer}
                  </div>
                )}
                
                <div style={{
                  padding: '15px',
                  backgroundColor: '#d1ecf1',
                  borderRadius: '6px',
                  fontSize: '14px',
                  borderLeft: '3px solid #17a2b8',
                  lineHeight: '1.8'
                }}>
                  <strong>{language === 'en' ? '✨ Optimized Version (Band 7-8): ' : '✨ 优化版本（7-8分水平）：'}</strong>
                  <div style={{ marginTop: '8px' }}>
                    {item.improvedAnswer || item.improved || (language === 'en' ? 'No optimization available' : '暂无优化版本')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // 兼容旧格式（对象格式）
    const improvedAnswer = typeof currentOptimizedAnswers === 'object' && currentOptimizedAnswers.improved
      ? currentOptimizedAnswers.improved
      : typeof currentOptimizedAnswers === 'string'
        ? currentOptimizedAnswers
        : language === 'en' ? 'No properly formatted answer available.' : '答案格式不正确。';
    
    return (
      <div className="answer-optimization">
        <h4>{language === 'en' ? 'Optimized Version' : '优化版本'}</h4>
        <div className="optimization-container">
          <div className="improved-answer">
            <div className="answer-content">
              {improvedAnswer}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <>
      {showLoginModal && (
        <LoginModal 
          onClose={handleCloseLoginModal} 
          onLogin={handleLoginSuccess} 
        />
      )}
      
      <div className="score-report-container">
        {!isLoggedIn ? (
          // 未登录状态，只显示登录提示
          <div className="login-required-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px' }}>{t('ielts_improvement_report')}</h2>
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '25px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '15px',
                fontSize: '18px'
              }}>
                {language === 'en' ? 'Login Required' : '需要登录'}
              </div>
              <div style={{ marginBottom: '20px' }}>
                {language === 'en' 
                  ? 'Please login to access the IELTS improvement report feature.' 
                  : '请登录以访问雅思提分报告功能。'}
              </div>
              <button 
                onClick={() => setShowLoginModal(true)} 
                style={{
                  padding: '10px 25px',
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '16px'
                }}
              >
                {language === 'en' ? 'Login' : '登录'}
              </button>
            </div>
          </div>
        ) : !isPaidMember ? (
          // 已登录但不是付费会员，显示升级提示
          <div className="membership-required-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px' }}>{t('ielts_improvement_report')}</h2>
            <div style={{ 
              backgroundColor: '#fff8e6', 
              border: '1px solid #ffe0a6',
              borderRadius: '8px',
              padding: '25px',
              maxWidth: '500px',
              width: '100%'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '15px',
                fontSize: '18px',
                color: '#e6a800'
              }}>
                {language === 'en' ? 'Premium Feature' : '高级功能'}
              </div>
              <div style={{ marginBottom: '20px' }}>
                {language === 'en' 
                  ? 'The IELTS Improvement Report is a premium feature. Please upgrade using a valid redemption code to access detailed analysis and personalized suggestions.' 
                  : '雅思提分报告是高级功能。请使用有效的兑换码升级为付费会员以获取详细分析和个性化建议。'}
              </div>
              <button 
                onClick={() => window.location.href = '/account'} 
                style={{
                  padding: '10px 25px',
                  backgroundColor: '#e6a800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '16px',
                  boxShadow: '0 2px 5px rgba(230, 168, 0, 0.3)'
                }}
              >
                {language === 'en' ? 'Upgrade with Redemption Code' : '使用兑换码升级'}
              </button>
            </div>
          </div>
        ) : loading ? (
          // 加载中状态 - 只显示一个简单的加载提示框
          <div className="loading-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px' }}>{t('ielts_improvement_report')}</h2>
            <div style={{ 
              backgroundColor: '#e2f0fd', 
              border: '1px solid #b8daff',
              borderRadius: '8px',
              padding: '25px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '10px',
                fontSize: '18px'
              }}>
                {t('report_generating')}
              </div>
              <div>{t('please_wait')}</div>
            </div>
          </div>
        ) : apiCallStatus.called && apiCallStatus.success ? (
          // 报告已生成，显示完整报告内容
          <>
            <div className="score-report-header">
              <h2>{t('ielts_improvement_report')} - {getModuleName()}</h2>
              <div className="report-info">
                <span>{username} · {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'zh-CN')}</span>
              </div>
            </div>
            
            <div className="report-tabs">
              <button 
                className={`tab-button ${showRecords ? 'active' : ''}`}
                onClick={switchToRecords}
              >
                {t('answer_records')}
              </button>
              <button 
                className={`tab-button ${showScores ? 'active' : ''}`}
                onClick={switchToScores}
              >
                {t('score_analysis')}
              </button>
              <button 
                className={`tab-button ${showSuggestions ? 'active' : ''}`}
                onClick={switchToSuggestions}
              >
                {t('answer_optimization')}
              </button>
            </div>
            
            <div className="report-content">
              {showRecords && (
                <div className="answer-records-section">
                  <h3 className="section-title">{t('answer_records')}</h3>
                  
                  {!currentRound ? (
                    <p className="no-records">{t('no_answer_records')}</p>
                  ) : (
                    <div className="records-list">
                      <div className="record-item">
                        <div className="record-header">
                          <span className="part-label">
                            {getModuleName()}
                          </span>
                          <span className="record-time">{currentRound.date} {currentRound.time}</span>
                        </div>
                        
                        <div className="messages-container">
                          {currentRound.messages.map((message, msgIndex) => (
                            <div key={msgIndex} className={`message ${message.type}`}>
                              <div className="message-header">
                                <span className="sender">
                                  {message.type === 'mentor' ? t('examiner') : username}
                                </span>
                                <span className="message-time">{message.time}</span>
                              </div>
                              
                              <div className="message-content">
                                {message.type === 'user' 
                                  ? <div dangerouslySetInnerHTML={{ __html: highlightGrammarErrors(message.content) }} />
                                  : message.content
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {showScores && (
                <div className="scores-section">
                  <h3 className="section-title">
                    <span>{t('score_analysis')}</span>
                  </h3>
                  
                  <div className="module-score-container" style={{ 
                    background: '#f8f9fa', 
                    padding: '20px', 
                    borderRadius: '8px',
                    marginBottom: '25px',
                    textAlign: 'center'
                  }}>
                    <div className="module-score-label" style={{ 
                      fontSize: '16px', 
                      fontWeight: '500', 
                      marginBottom: '10px' 
                    }}>
                      {getModuleName()}
                    </div>
                    <div className={`module-score-value ${getScoreColor(currentModuleScore.total)}`} style={{
                      fontSize: '36px',
                      fontWeight: '700',
                      color: getScoreColor(currentModuleScore.total) === 'green' ? '#28a745' : 
                             getScoreColor(currentModuleScore.total) === 'blue' ? '#007bff' :
                             getScoreColor(currentModuleScore.total) === 'orange' ? '#ffc107' : '#dc3545'
                    }}>
                      {currentModuleScore.total.toFixed(1)}
                    </div>
                  </div>
                  
                  <div className="detailed-scores">
                    <h4>{t('score_details')}</h4>
                    
                    <div className="score-dimension-container">
                      <div className="score-dimension">
                        <div className="dimension-header">
                          <span className="dimension-label">{t('fluency_coherence')} (35%)</span>
                          <span className={`dimension-score ${getScoreColor(currentModuleScore.fluency)}`}>
                            {currentModuleScore.fluency.toFixed(1)}
                          </span>
                        </div>
                        <div className="score-bar-container">
                          <div 
                            className={`score-bar ${getScoreColor(currentModuleScore.fluency)}`}
                            style={{ width: `${getScorePercentage(currentModuleScore.fluency)}%` }}
                          ></div>
                        </div>
                        <div className="dimension-analysis">
                          <h5>{t('analysis')}</h5>
                          <p>{currentAnalysis.fluency}</p>
                        </div>
                      </div>
                      
                      <div className="score-dimension">
                        <div className="dimension-header">
                          <span className="dimension-label">{t('lexical_resource')} (35%)</span>
                          <span className={`dimension-score ${getScoreColor(currentModuleScore.vocabulary)}`}>
                            {currentModuleScore.vocabulary.toFixed(1)}
                          </span>
                        </div>
                        <div className="score-bar-container">
                          <div 
                            className={`score-bar ${getScoreColor(currentModuleScore.vocabulary)}`}
                            style={{ width: `${getScorePercentage(currentModuleScore.vocabulary)}%` }}
                          ></div>
                        </div>
                        <div className="dimension-analysis">
                          <h5>{t('analysis')}</h5>
                          <p>{currentAnalysis.vocabulary}</p>
                        </div>
                      </div>
                      
                      <div className="score-dimension">
                        <div className="dimension-header">
                          <span className="dimension-label">{t('grammatical_range')} (30%)</span>
                          <span className={`dimension-score ${getScoreColor(currentModuleScore.grammar)}`}>
                            {currentModuleScore.grammar.toFixed(1)}
                          </span>
                        </div>
                        <div className="score-bar-container">
                          <div 
                            className={`score-bar ${getScoreColor(currentModuleScore.grammar)}`}
                            style={{ width: `${getScorePercentage(currentModuleScore.grammar)}%` }}
                          ></div>
                        </div>
                        <div className="dimension-analysis">
                          <h5>{t('analysis')}</h5>
                          <p style={{ whiteSpace: 'pre-line' }}>{currentAnalysis.grammar}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {showSuggestions && (
                <div className="suggestions-section">
                  <h3 className="section-title">{t('answer_optimization')}</h3>
                  
                  <div className="practice-suggestion">
                    <h4>{t('overall_feedback')}</h4>
                    <div className="practice-message">
                      <p className="overall-feedback">
                        {overallFeedback || (language === 'en' 
                          ? `Based on your current ${getModuleName()} performance, here's how your answers could be improved to reach Band 7+ level.`
                          : `根据您在${getModuleName()}的表现，以下是如何提升您的回答以达到7+分水平的建议。`)}
                      </p>
                    </div>
                  </div>
                  
                  {renderAnswerOptimization()}
                </div>
              )}
            </div>
            
            <div className="report-footer">
              <p>{t('report_reference_note')}</p>
            </div>
          </>
        ) : (
          // 已登录且是有效付费会员，但报告尚未生成完成或未开始生成
          // 这里直接显示加载中状态，移除多余的"生成报告"按钮界面
          <div className="loading-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px' }}>{t('ielts_improvement_report')}</h2>
            <div style={{ 
              backgroundColor: '#e2f0fd', 
              border: '1px solid #b8daff',
              borderRadius: '8px',
              padding: '25px',
              maxWidth: '400px',
              width: '100%'
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                marginBottom: '10px',
                fontSize: '18px'
              }}>
                {t('report_generating')}
              </div>
              <div>{t('please_wait')}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ScoreReport; 
